// Dynamic 256-colour physical palette, applied to the 8-bit framebuffer.
// Ported from the Set*Palette functions in levelblit.c.

import { G, rooms } from '../core/state';

const M_PI = Math.PI;
const tr = Math.trunc;

// Physical palette: 256 entries * 3 (r,g,b). Uint8 to mirror SDL_Color wrap.
export const physPal = new Uint8Array(256 * 3);

let palDirty = true;

// Precomputed greyscale curve samples (index 0..255).
const PAL_COS = new Float64Array(256);
const PAL_SIN = new Float64Array(256);
for (let i = 0; i < 256; i++) {
  const ip = i / 255.0;
  PAL_COS[i] = (Math.cos((ip * M_PI) / 2.0 + M_PI) + 1.0) * 255;
  PAL_SIN[i] = Math.sin((ip * M_PI) / 2.0) * 255;
}

export function markPalDirty(): void {
  palDirty = true;
}

export function consumePalDirty(): boolean {
  if (!palDirty) return false;
  palDirty = false;
  return true;
}

export function setPal(i: number, r: number, g: number, b: number): void {
  physPal[i * 3] = tr(r) & 255;
  physPal[i * 3 + 1] = tr(g) & 255;
  physPal[i * 3 + 2] = tr(b) & 255;
  palDirty = true;
}

export function SetGreyscalePalette(): void {
  for (let i = 0; i < 256; i++) {
    const r = PAL_COS[i];
    const g = (PAL_SIN[i] + i) / 2;
    const b = PAL_SIN[i];
    setPal(i, r, g, b);
  }
}

export function SetTitlePalette(curve_start: number, curve_end: number): void {
  for (let i = 0; i < 256; i++) {
    let ec = ((i - curve_start) * 255) / (curve_end - curve_start);
    ec = tr(ec);
    if (ec < 0) ec = 0;
    if (ec > 255) ec = 255;
    setPal(i, ec, ec, ec);
  }
}

let stp_tk = 0;

// levelblit.c SetTonedPalette: dynamic room toning by depth, enemy density,
// artifact/curse and boss-fight states.
export function SetTonedPalette(dct: number): void {
  let pct = 1.0 - dct;
  const ec = rooms[G.player_room].enemies;
  let rp_dct: number;
  if (ec < 50) rp_dct = ec / 50.0;
  else rp_dct = 1.0;
  const rp_pct = 1.0 - rp_dct;

  if (G.player_room === 0 && G.current_boss === 3 && G.boss_fight_mode >= 3) {
    if (G.boss_fight_mode === 23) {
      for (let i = 0; i < 256; i++) setPal(i, i, i, i);
    } else {
      stp_tk++;
      pct =
        Math.sin((stp_tk / 20.0) * M_PI) * (0.5 - (G.boss_fight_mode - 3) * 0.025) +
        (0.5 - (G.boss_fight_mode - 3) * 0.025);
      if (G.magic_circuit < 0.1) pct = 1.0;
      for (let i = 0; i < 256; i++) {
        const r = 255 - (255 - PAL_COS[i]) * pct;
        const g = 255 - (255 - i) * pct;
        const b = 255 - (255 - PAL_SIN[i]) * pct;
        setPal(i, r, g, b);
      }
      setPal(1, 0, 0, 0);
    }
  } else {
    if (G.artifacts[11]) {
      if (G.player_room === 0) {
        stp_tk++;
        pct = Math.sin((stp_tk / 33.0) * M_PI) * 0.5 + 0.5;
        for (let i = 0; i < 256; i++) setPal(i, i, tr(i / 3) * pct, tr((i * 2) / 3) * pct);
      } else {
        for (let i = 0; i < 256; i++) {
          setPal(i, i, i * dct, PAL_COS[i] * dct);
        }
      }
      if (G.current_boss === 3 && G.player_shield === 30 && G.player_room === 0) {
        if (G.boss_lives <= 1) {
          stp_tk++;
          for (let i = 0; i < 256; i++) {
            const p = Math.sin(((stp_tk + i) / 24.0) * M_PI) * 0.5 + 0.5;
            setPal(i, (i * 0.5 + 128) * p, i * 0.5 + 128, (i * 0.5 + 128) * p);
          }
        }
      }
    } else {
      for (let i = 0; i < 256; i++) {
        const r =
          (PAL_COS[i] * pct + i * dct) * rp_pct + (PAL_SIN[i] * 207 + 48) * rp_dct;
        const g = i * rp_pct + PAL_COS[i] * rp_dct;
        const b =
          (PAL_SIN[i] * pct + PAL_COS[i] * dct) * rp_pct + PAL_COS[i] * rp_dct;
        setPal(i, r, g, b);
      }
    }
  }
}

export function SetTitlePalette2(t: number): void {
  let bright = 1 - t / 30.0;
  if (bright < 0.0) bright = 0.0;
  const b_coeff = 1 - bright;
  for (let i = 0; i < 256; i++) {
    const r = PAL_COS[i] * b_coeff + 255 * bright;
    const g = ((PAL_SIN[i] + i) / 2) * b_coeff + 255 * bright;
    const b = PAL_SIN[i] * b_coeff + 255 * bright;
    setPal(i, r, g, b);
  }
}
